import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FiTrendingUp, FiTrendingDown, FiUsers } from 'react-icons/fi';
import axios from 'axios';

const CustomerTrendGraph = () => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [animationPhase, setAnimationPhase] = useState(0);

    const softColors = {
        increase: {
            primary: '#10b981',
            light: '#d1fae5',
            gradient: 'linear-gradient(to top, #10b981, #34d399)',
            shadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
        },
        decrease: {
            primary: '#ef4444',
            light: '#fee2e2',
            gradient: 'linear-gradient(to top, #ef4444, #f87171)',
            shadow: '0 2px 8px rgba(239, 68, 68, 0.3)'
        },
        neutral: {
            primary: '#6b7280',
            light: '#f3f4f6',
            gradient: 'linear-gradient(to top, #6b7280, #9ca3af)',
            shadow: '0 2px 8px rgba(107, 114, 128, 0.3)'
        },
        today: {
            primary: '#3b82f6',
            light: '#dbeafe',
            gradient: 'linear-gradient(to top, #3b82f6, #60a5fa)',
            shadow: '0 2px 12px rgba(59, 130, 246, 0.4)'
        },
        pastel: ['#a78bfa', '#f472b6', '#38bdf8', '#34d399', '#fbbf24'],
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    };

    const fetchCustomers = useCallback(async () => {
        try {
            setLoading(true);
            const canAccess = JSON.parse(localStorage.getItem("can_access") || "0");
            const userName = localStorage.getItem("user_name") || "";

            let response;
            if (canAccess === 1) {
                response = await axios.get("https://www.azzenterprises.com/api/customers", {
                    withCredentials: true
                });
            } else {
                response = await axios.get(`https://www.azzenterprises.com/api/customers/bystaff/${userName}`, {
                    withCredentials: true
                });
            }

            const responseData = response?.data;
            let customersData = [];

            if (Array.isArray(responseData)) {
                customersData = responseData;
            } else if (responseData && typeof responseData === 'object') {
                if (Array.isArray(responseData.customers)) {
                    customersData = responseData.customers;
                } else if (Array.isArray(responseData.data)) {
                    customersData = responseData.data;
                } else {
                    const possibleArrays = Object.values(responseData).filter(item => Array.isArray(item));
                    customersData = possibleArrays[0] || [];
                }
            }
            setCustomers(customersData);
        } catch (error) {
            console.error('❌ Error fetching customers:', error);
            setCustomers([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCustomers();
        const timeoutId = setTimeout(fetchCustomers, 1000);
        return () => clearTimeout(timeoutId);
    }, [fetchCustomers]);

    useEffect(() => {
        const handleStorageChange = () => fetchCustomers();
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [fetchCustomers]);

    useEffect(() => {
        const interval = setInterval(() => {
            setAnimationPhase(prev => (prev + 1) % 4);
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    const getWeekData = useMemo(() => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        const thisWeekStart = new Date(today);
        thisWeekStart.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
        const thisWeekEnd = new Date(thisWeekStart);
        thisWeekEnd.setDate(thisWeekStart.getDate() + 6);

        const lastWeekStart = new Date(thisWeekStart);
        lastWeekStart.setDate(thisWeekStart.getDate() - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);

        const thisWeekCustomers = customers.filter(customer => {
            const timestamp = customer.timestamp || customer.created_at || customer.created_date || customer.date;
            if (!timestamp) return false;
            const customerDate = new Date(timestamp);
            return !isNaN(customerDate) && customerDate >= thisWeekStart && customerDate <= thisWeekEnd;
        });

        const lastWeekCustomers = customers.filter(customer => {
            const timestamp = customer.timestamp || customer.created_at || customer.created_date || customer.date;
            if (!timestamp) return false;
            const customerDate = new Date(timestamp);
            return !isNaN(customerDate) && customerDate >= lastWeekStart && customerDate <= lastWeekEnd;
        });

        const thisWeekDays = [];
        for (let i = 0; i < 7; i++) {
            const day = new Date(thisWeekStart);
            day.setDate(thisWeekStart.getDate() + i);

            const dayCustomers = thisWeekCustomers.filter(customer => {
                const timestamp = customer.timestamp || customer.created_at || customer.created_date || customer.date;
                if (!timestamp) return false;
                const customerDate = new Date(timestamp);
                if (isNaN(customerDate)) return false;
                return customerDate.getDate() === day.getDate() &&
                    customerDate.getMonth() === day.getMonth() &&
                    customerDate.getFullYear() === day.getFullYear();
            });

            thisWeekDays.push({
                day: day.toLocaleDateString('en-US', { weekday: 'short' }),
                date: day.getDate(),
                count: dayCustomers.length,
                customers: dayCustomers,
                isToday: day.getDate() === today.getDate(),
                color: softColors.pastel[i % softColors.pastel.length]
            });
        }

        const thisWeekTotal = thisWeekCustomers.length;
        const lastWeekTotal = lastWeekCustomers.length;

        let percentageChange = 0;
        let isIncrease = true;

        if (lastWeekTotal > 0) {
            percentageChange = ((thisWeekTotal - lastWeekTotal) / lastWeekTotal) * 100;
            isIncrease = thisWeekTotal >= lastWeekTotal;
        } else if (thisWeekTotal > 0) {
            percentageChange = 100;
            isIncrease = true;
        }

        return {
            thisWeek: {
                total: thisWeekTotal,
                days: thisWeekDays,
                start: thisWeekStart,
                end: thisWeekEnd
            },
            lastWeek: { total: lastWeekTotal },
            percentageChange: Math.abs(percentageChange).toFixed(1),
            isIncrease,
            todayCount: thisWeekDays.find(day => day.isToday)?.count || 0
        };
    }, [customers]);

    if (loading) {
        return (
            <div className="d-flex align-items-center gap-2 p-2 rounded-3 border" style={{ minWidth: '400px', height: '82px' }}>
                <motion.div
                    className="skeleton-line flex-grow-1"
                    style={{ height: '50px', borderRadius: '8px' }}
                    animate={{
                        background: [
                            'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                            'linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%)',
                            'linear-gradient(90deg, #f0f0f0 75%, #e0e0e0 100%, #f0f0f0 125%)'
                        ]
                    }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                />
            </div>
        );
    }

    const { thisWeek, percentageChange, isIncrease } = getWeekData;
    const maxValue = Math.max(...(thisWeek.days?.map(d => d.count) || [0]), 1);

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="d-flex align-items-center gap-3 p-3 rounded-3 border shadow-sm position-relative overflow-hidden"
            style={{
                background: softColors.background,
                border: '1px solid #e2e8f0',
                minWidth: '400px',
                height: '82px'
            }}
        >
            <motion.div
                className="position-absolute"
                style={{
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'radial-gradient(circle at 20% 50%, rgba(168, 85, 247, 0.05) 0%, transparent 50%)',
                    zIndex: 0
                }}
                animate={{
                    x: [0, 50, 0],
                    y: [0, 25, 0]
                }}
                transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
            />

            <div className="d-flex flex-column z-1" style={{ minWidth: '120px' }}>
                <div className="d-flex align-items-center gap-2 mb-2">
                    <motion.div
                        className="rounded-circle d-flex align-items-center justify-content-center position-relative"
                        style={{
                            width: '32px',
                            height: '32px',
                            backgroundColor: isIncrease ? softColors.increase.light : softColors.decrease.light,
                            color: isIncrease ? softColors.increase.primary : softColors.decrease.primary
                        }}
                        animate={{
                            scale: [1, 1.05, 1]
                        }}
                        transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                    >
                        <FiUsers size={18} />
                        <motion.div
                            className="position-absolute rounded-circle"
                            style={{
                                width: '36px',
                                height: '36px',
                                border: `1.5px solid ${isIncrease ? softColors.increase.primary : softColors.decrease.primary}`,
                                opacity: 0.2
                            }}
                            animate={{
                                scale: [1, 1.2, 1],
                                opacity: [0.2, 0, 0.2]
                            }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                        />
                    </motion.div>

                    <div>
                        <motion.div
                            className="fw-bold"
                            style={{ fontSize: '1.2rem' }}
                            animate={{
                                y: [0, -1, 0]
                            }}
                            transition={{
                                duration: 4,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                        >
                            {thisWeek.total}
                        </motion.div>
                        <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                            This Week
                        </small>
                    </div>
                </div>

                <motion.div
                    className="d-flex align-items-center gap-1"
                    animate={{
                        x: [0, 1, 0, -1, 0]
                    }}
                    transition={{
                        duration: 6,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                >
                    <span style={{
                        fontSize: '0.75rem',
                        color: isIncrease ? softColors.increase.primary : softColors.decrease.primary,
                        fontWeight: 500
                    }}>
                        {isIncrease ? '+' : '-'}{percentageChange}%
                    </span>
                    <span className="text-muted" style={{ fontSize: '0.7rem' }}>
                        vs last week
                    </span>
                </motion.div>
            </div>

            {/* Middle Section - Graph with Clean Animations */}
            <div className="flex-grow-1 z-1" style={{ minWidth: '200px' }}>
                <div className="d-flex justify-content-between align-items-end h-100">
                    {thisWeek.days?.map((day, index) => {
                        const isToday = day.isToday;
                        const barColor = isToday ? softColors.today.gradient : day.color;
                        const barHeight = Math.max((day.count / maxValue) * 100, 4);

                        const animationConfig = {
                            0: { scaleY: [1, 1.03, 1], transition: { duration: 0.8, ease: "easeInOut" } },
                            1: { y: [0, -1, 0], transition: { duration: 0.6, ease: "easeInOut" } },
                            2: { opacity: [1, 0.85, 1], transition: { duration: 0.7, ease: "easeInOut" } },
                            3: { scaleY: [1, 0.98, 1], transition: { duration: 0.9, ease: "easeInOut" } }
                        }[animationPhase];

                        return (
                            <motion.div
                                key={index}
                                className="d-flex flex-column align-items-center position-relative"
                                style={{ width: '12%' }}
                                animate={animationConfig}
                            >
                                <motion.div
                                    className="rounded-top position-relative"
                                    style={{
                                        width: '100%',
                                        minHeight: '4px',
                                        height: `${barHeight}%`,
                                        background: barColor,
                                        boxShadow: isToday ? softColors.today.shadow : '0 1px 2px rgba(0,0,0,0.05)'
                                    }}
                                    animate={{
                                        height: [
                                            `${barHeight}%`,
                                            `${Math.min(100, barHeight + 2)}%`,
                                            `${barHeight}%`
                                        ],
                                        filter: [
                                            'brightness(1)',
                                            'brightness(1.1)',
                                            'brightness(1)'
                                        ]
                                    }}
                                    transition={{
                                        duration: 4,
                                        repeat: Infinity,
                                        ease: "easeInOut"
                                    }}
                                >
                                    {isToday && day.count > 0 && (
                                        <motion.div
                                            className="position-absolute rounded-circle"
                                            style={{
                                                width: '3px',
                                                height: '3px',
                                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                                top: '2px',
                                                left: '50%',
                                                transform: 'translateX(-50%)'
                                            }}
                                            animate={{
                                                y: [0, -8, 0],
                                                opacity: [0, 0.8, 0]
                                            }}
                                            transition={{
                                                duration: 3,
                                                repeat: Infinity,
                                                delay: index * 0.1,
                                                ease: "easeInOut"
                                            }}
                                        />
                                    )}
                                </motion.div>

                                <div className="mt-1 text-center">
                                    <motion.div
                                        style={{
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            color: isToday ? softColors.today.primary : '#374151'
                                        }}
                                        animate={{
                                            scale: [1, 1.02, 1]
                                        }}
                                        transition={{
                                            duration: 3,
                                            repeat: Infinity,
                                            delay: index * 0.1,
                                            ease: "easeInOut"
                                        }}
                                    >
                                        {day.count}
                                    </motion.div>
                                    <motion.div
                                        style={{
                                            fontSize: '0.6rem',
                                            color: isToday ? softColors.today.primary : '#64748b'
                                        }}
                                        animate={{
                                            opacity: [0.8, 1, 0.8]
                                        }}
                                        transition={{
                                            duration: 4,
                                            repeat: Infinity,
                                            ease: "easeInOut"
                                        }}
                                    >
                                        {day.day}
                                    </motion.div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>

            {/* Right Section - Info with Gentle Glow */}
            <div className="d-flex flex-column gap-1 z-1" style={{ minWidth: '60px' }}>
                <motion.div
                    className="text-center position-relative"
                    animate={{
                        scale: [1, 1.01, 1],
                        color: ['#6b7280', '#4b5563', '#6b7280']
                    }}
                    transition={{
                        duration: 5,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                >
                    {thisWeek.todayCount > 0 && (
                        <motion.div
                            className="position-absolute rounded-circle"
                            style={{
                                width: '30px',
                                height: '30px',
                                backgroundColor: softColors.today.primary,
                                opacity: 0.05,
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)'
                            }}
                            animate={{
                                scale: [1, 1.3, 1]
                            }}
                            transition={{
                                duration: 3,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                        />
                    )}
                    <div className="fw-bold position-relative" style={{ fontSize: '0.8rem' }}>
                        {thisWeek.todayCount}
                    </div>
                    <div className="text-muted position-relative" style={{ fontSize: '0.6rem' }}>
                        Today
                    </div>
                </motion.div>

                <motion.div
                    className="mt-1 text-center"
                    animate={{
                        opacity: [0.7, 1, 0.7]
                    }}
                    transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                >
                    <div className="text-muted" style={{ fontSize: '0.55rem' }}>
                        Live
                    </div>
                    <motion.div
                        className="rounded-circle d-inline-block"
                        style={{
                            width: '6px',
                            height: '6px',
                            backgroundColor: '#10b981'
                        }}
                        animate={{
                            scale: [1, 1.2, 1],
                            opacity: [0.8, 1, 0.8]
                        }}
                        transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                    />
                </motion.div>
            </div>
        </motion.div>
    );
};

export default CustomerTrendGraph;

